import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import Button from "@/components/base/Button";
import { useT } from "@/lib/useSiteText";
import type { SiteTextKey } from "@/lib/siteTexts";

const navItems: { to: string; key: SiteTextKey }[] = [
  { to: "/bodegas", key: "comun.nav.bodegas" },
  { to: "/planes", key: "comun.nav.planes" },
  { to: "/ubicacion", key: "comun.nav.ubicacion" },
  { to: "/seguridad", key: "comun.nav.seguridad" },
  { to: "/nosotros", key: "comun.nav.nosotros" },
  { to: "/preguntas-frecuentes", key: "comun.nav.faq" },
];

export default function Header() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || menuOpen;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${data.siteSettings.brand} - Inicio`}>
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-store-2-line text-lg" />
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className={`font-heading font-bold text-base md:text-lg whitespace-nowrap transition-colors ${
                  solid ? "text-foreground-900" : "text-background-50"
                }`}
              >
                {data.siteSettings.brand}
              </span>
              <span
                className={`text-[11px] whitespace-nowrap transition-colors ${
                  solid ? "text-foreground-500" : "text-background-300"
                }`}
              >
                {data.siteSettings.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-sm font-medium transition whitespace-nowrap ${
                    isActive
                      ? "text-primary-500"
                      : solid
                        ? "text-foreground-700 hover:text-primary-600"
                        : "text-background-100 hover:text-background-50"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
              className={`w-10 h-10 rounded-md flex items-center justify-center transition border ${
                solid
                  ? "border-background-300 text-foreground-700 hover:text-primary-600 hover:border-primary-300"
                  : "border-background-50/30 text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className="ri-whatsapp-line text-lg" />
            </a>
            <Button to="/bodegas" icon="ri-store-2-line">
              {t("comun.nav.boton")}
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center transition ${
              solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
            }`}
            aria-label="Abrir menú"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 md:px-6 py-4">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `py-3 text-sm font-medium border-b border-background-100 ${
                    isActive ? "text-primary-600" : "text-foreground-700"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <NavLink
              to="/contacto"
              className="py-3 text-sm font-medium border-b border-background-100 text-foreground-700"
            >
              {t("comun.nav.contacto")}
            </NavLink>
          </nav>
          <div className="flex gap-3 pt-4">
            <Button to="/bodegas" className="flex-1">
              {t("comun.nav.boton")}
            </Button>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              className="w-12 h-12 rounded-md bg-accent-500 text-background-50 flex items-center justify-center"
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-lg" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}